import { format } from 'date-fns';
import { FilingStatus } from 'api/tdsFilingData';

export const paiseToRupees = (paise: number | null | undefined): string => {
  if (paise === null || paise === undefined) return '—';
  const rupees = paise / 100;
  return `₹${rupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatEpochDate = (epoch: number | null | undefined): string => {
  if (!epoch) return '—';
  return format(new Date(epoch * 1000), 'dd/MM/yyyy');
};

export const formatEpochDateTime = (epoch: number | null | undefined): string => {
  if (!epoch) return '—';
  return format(new Date(epoch * 1000), 'dd/MM/yyyy HH:mm');
};

export type BadgeColor =
  | 'neutral'
  | 'primary'
  | 'information'
  | 'notice'
  | 'positive'
  | 'negative';

export const STATUS_BADGE_COLOR: Record<FilingStatus, BadgeColor> = {
  // tax-compliance parse statuses
  requested: 'neutral',
  generation_completed: 'information',
  generation_failed: 'notice',
  processing: 'notice',
  completed: 'positive',
  failed: 'negative',
  // pipeline statuses
  filing_data_generation_triggered: 'neutral',
  filing_data_generation_started: 'neutral',
  filing_data_generation_completed: 'information',
  filing_data_generation_failed: 'negative',
  filing_data_generation_skipped: 'neutral',
  // downstream statuses
  fvu_generation_triggered: 'information',
  fvu_generation_completed: 'information',
  fvu_generation_failed: 'negative',
  tds_filing_triggered: 'information',
  tds_filing_completed: 'positive',
  tds_filing_failed: 'negative',
};

export const STATUS_LABEL: Record<FilingStatus, string> = {
  // tax-compliance parse statuses
  requested: 'Requested',
  generation_completed: 'Generation Completed',
  generation_failed: 'Generation Failed',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
  // pipeline statuses
  filing_data_generation_triggered: 'Generation Triggered',
  filing_data_generation_started: 'Generation Started',
  filing_data_generation_completed: 'Generation Completed',
  filing_data_generation_failed: 'Generation Failed',
  filing_data_generation_skipped: 'Generation Skipped',
  // downstream statuses
  fvu_generation_triggered: 'FVU Triggered',
  fvu_generation_completed: 'FVU Completed',
  fvu_generation_failed: 'FVU Failed',
  tds_filing_triggered: 'Filing Triggered',
  tds_filing_completed: 'Filing Completed',
  tds_filing_failed: 'Filing Failed',
};

/**
 * Returns the default quarter and FY based on the current month.
 * Filing deadlines:
 *   Q1 (Apr–Jun)  → due July       → months 7,8,9   default Q1
 *   Q2 (Jul–Sep)  → due October    → months 10,11,12 default Q2
 *   Q3 (Oct–Dec)  → due January    → months 1,2,3    default Q3
 *   Q4 (Jan–Mar)  → due April/May  → months 4,5,6    default Q4
 */
export const getDefaultQuarterAndFY = (): { quarter: string; fy: string } => {
  const now = new Date();
  const month = now.getMonth() + 1; // 1-indexed
  const year  = now.getFullYear();

  let quarter: string;
  let fyStart: number;

  if (month >= 7 && month <= 9) {
    quarter = 'Q1';
    fyStart = year;          // Q1 Apr–Jun of current FY starting this April
  } else if (month >= 10) {
    quarter = 'Q2';
    fyStart = year;
  } else if (month <= 3) {
    quarter = 'Q3';
    fyStart = year - 1;      // Q3 Oct–Dec was in the previous calendar year's FY
  } else {
    // months 4, 5, 6 → Q4 filing
    quarter = 'Q4';
    fyStart = year - 1;      // Q4 Jan–Mar was in the previous FY
  }

  const fy = `${fyStart}-${String(fyStart + 1).slice(-2)}`;
  return { quarter, fy };
};

export const FILING_POLL_INTERVAL_MS = 10_000;
export const FILING_POLL_MAX_ATTEMPTS = 18;

export const TERMINAL_STATUSES: FilingStatus[] = [
  'completed', 'failed', 'generation_failed',
  'filing_data_generation_failed', 'filing_data_generation_skipped',
  'tds_filing_completed', 'tds_filing_failed', 'fvu_generation_failed',
];

export const PROCESSING_STATUSES: FilingStatus[] = [
  // parse phase
  'requested', 'generation_completed', 'processing',
  // pipeline (job in queue or running)
  'filing_data_generation_triggered', 'filing_data_generation_started',
  // downstream (FVU + filing in progress)
  'fvu_generation_triggered', 'tds_filing_triggered',
];
